import { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const articleId = parseInt(id || '0', 10);
  const article = articles.find((a) => a.id === articleId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category ? category.name : article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '白洁情感文学精选',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://up-tv.cn/article/${article.id}`,
    },
    keywords: article.tags.join(','),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main className="min-h-screen pt-20 pb-14">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="text-xs text-primary-500 font-label bg-primary-50 px-2.5 py-1 rounded-full hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {categoryName}
              </Link>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-500">{article.readTime}</span>
            </div>
            <h1 className="text-xl md:text-3xl font-heading font-semibold text-foreground-900 leading-tight mb-3">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-4">
              <span className="text-xs text-foreground-500">作者：{article.author}</span>
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.imageUrlFeature}
              alt={article.title}
              title={`${article.title} - 白洁情感文学精选`}
              className="w-full object-cover max-h-[450px]"
            />
          </div>

          <div
            className="prose prose-base max-w-none text-foreground-800 leading-relaxed [&_p]:mb-4 [&_p]:text-sm md:[&_p]:text-base [&_p]:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-8 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2 mb-6">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-foreground-600 bg-background-100 px-3 py-1 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} article={related} variant="compact" />
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}